import React from "react";
import { Box } from "@mui/material";
import { configGrafico, PlotAcotado } from "../CalidadDatos/plotConfig";

const SERIE_1 = "#2a78d6";
const SERIE_2 = "#eb6834";
const CAJA_RELLENO = "#cde2fb";
const BANDA_REFERENCIA = "#f1efe8";

const SECUENCIAL_AZUL = [
  [0, "#cde2fb"],
  [0.17, "#9ec5f4"],
  [0.34, "#6da7ec"],
  [0.5, "#3987e5"],
  [0.67, "#256abf"],
  [0.84, "#184f95"],
  [1, "#0d366b"],
];

const COLOR_RESOLUCION = {
  Aprobado: "#0ca30c",
  "En trámite": "#fab219",
  Rechazado: "#d03b3b",
};

const PATRON_RESOLUCION = {
  Aprobado: "",
  "En trámite": "/",
  Rechazado: "x",
};

const ORDEN_RESOLUCION = ["Aprobado", "En trámite", "Rechazado"];

const TINTA = "#0b0b0b";
const TINTA_SEC = "#52514e";
const TINTA_TENUE = "#898781";
const REJILLA = "#e1e0d9";
const SUPERFICIE = "#ffffff";

const FUENTE_TITULO = { size: 12, color: TINTA, family: "inherit" };
const FUENTE_EJE = { size: 10, color: TINTA_TENUE, family: "inherit" };
const FUENTE_ETIQUETA = { size: 10, color: TINTA_SEC, family: "inherit" };

function fmt(valor, sufijo = "", decimales = null) {
  if (valor == null) return "-";
  const numero = Number(valor);
  let d = decimales;
  if (d == null) {
    if (Number.isInteger(numero)) d = 0;
    else if (Math.abs(numero) >= 100) d = 0;
    else if (Math.abs(numero) >= 10) d = 1;
    else d = 2;
  }
  return (
    numero.toLocaleString("es-CL", {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }) + sufijo
  );
}

function recortar(texto, largo = 24) {
  const valor = String(texto ?? "");
  return valor.length <= largo ? valor : `${valor.slice(0, largo - 1)}…`;
}

function altoBarras(filas) {
  return Math.max(150, 34 * filas + 96);
}

function altoCajas(filas) {
  return Math.max(170, 40 * filas + 96);
}

function layoutBase(titulo, alto, extra = {}) {
  return {
    title: {
      text: titulo,
      font: FUENTE_TITULO,
      x: 0,
      xanchor: "left",
      y: 0.97,
      yanchor: "top",
    },
    height: alto,
    margin: { l: 10, r: 60, t: 42, b: 34 },
    paper_bgcolor: SUPERFICIE,
    plot_bgcolor: SUPERFICIE,
    showlegend: false,
    bargap: 0.35,
    font: { family: "inherit" },
    hoverlabel: { bgcolor: SUPERFICIE, bordercolor: REJILLA, font: FUENTE_ETIQUETA },
    xaxis: {
      gridcolor: REJILLA,
      zeroline: false,
      tickfont: FUENTE_EJE,
      automargin: true,
      separatethousands: true,
    },
    yaxis: {
      tickfont: FUENTE_ETIQUETA,
      automargin: true,
      ticks: "",
      showgrid: false,
      autorange: "reversed",
    },
    ...extra,
  };
}

function ejeCategorias(etiquetas) {
  return {
    categoryorder: "array",
    categoryarray: etiquetas,
    tickfont: FUENTE_ETIQUETA,
    automargin: true,
    ticks: "",
    showgrid: false,
    autorange: "reversed",
  };
}

function Grafico({ data, layout, nombre, alto }) {
  return (
    <PlotAcotado
      data={data}
      layout={layout}
      config={configGrafico(["reporte_contingencias", nombre], { alto })}
      useResizeHandler
      style={{ width: "100%", height: "100%" }}
    />
  );
}

function Paneles({ children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 1,
        px: 1,
        pb: 1,
      }}
    >
      {children}
    </Box>
  );
}

function Envoltura({ children }) {
  return <Box sx={{ px: 1, pb: 1 }}>{children}</Box>;
}

function barrasSimples(etiquetas, valores, color, sufijo, decimales) {
  return [
    {
      type: "bar",
      orientation: "h",
      y: etiquetas.map((e) => recortar(e)),
      x: valores,
      marker: { color },
      text: valores.map((v) => fmt(v, sufijo, decimales)),
      textposition: "outside",
      textfont: FUENTE_ETIQUETA,
      cliponaxis: false,
      hovertemplate: "%{y}: %{text}<extra></extra>",
    },
  ];
}

function trazasCaja(etiquetas, resumenes) {
  const conDatos = resumenes
    .map((resumen, indice) => ({ resumen, indice }))
    .filter(({ resumen }) => resumen && resumen.mediana != null && resumen.q1 != null);

  if (!conDatos.length) return null;

  const trazas = [
    {
      type: "box",
      orientation: "h",
      y: conDatos.map(({ indice }) => etiquetas[indice]),
      q1: conDatos.map(({ resumen }) => resumen.q1),
      median: conDatos.map(({ resumen }) => resumen.mediana),
      q3: conDatos.map(({ resumen }) => resumen.q3),
      lowerfence: conDatos.map(({ resumen }) => resumen.bigote_inferior),
      upperfence: conDatos.map(({ resumen }) => resumen.bigote_superior),
      fillcolor: CAJA_RELLENO,
      line: { color: SERIE_1, width: 1.2 },
      marker: { color: SERIE_1 },
      hoverinfo: "x+y",
      showlegend: false,
    },
  ];

  const atipicosX = [];
  const atipicosY = [];
  conDatos.forEach(({ resumen, indice }) => {
    (resumen.atipicos || []).forEach((valor) => {
      atipicosX.push(valor);
      atipicosY.push(etiquetas[indice]);
    });
  });

  if (atipicosX.length) {
    trazas.push({
      type: "scatter",
      mode: "markers",
      x: atipicosX,
      y: atipicosY,
      marker: { color: SERIE_1, size: 6, opacity: 0.45 },
      hovertemplate: "%{x}<extra>fuera de norma</extra>",
      showlegend: false,
    });
  }

  return trazas;
}

function PanelCajas({ titulo, etiquetas, resumenes, nombre, referenciaCero = false }) {
  const trazas = trazasCaja(etiquetas, resumenes);
  if (!trazas) return null;

  const alto = altoCajas(etiquetas.length);
  const layout = layoutBase(titulo, alto, {
    yaxis: ejeCategorias(etiquetas),
    margin: { l: 10, r: 40, t: 42, b: 34 },
  });

  if (referenciaCero) {
    layout.shapes = [
      {
        type: "line",
        x0: 0,
        x1: 0,
        yref: "paper",
        y0: 0,
        y1: 1,
        line: { color: TINTA_TENUE, width: 1 },
      },
    ];
  }

  return <Grafico data={trazas} layout={layout} nombre={nombre} alto={alto} />;
}

export function GraficoPorCategoria({ reporte }) {
  const filas = reporte?.por_categoria || [];
  if (filas.length < 2) return null;

  const etiquetas = filas.map((f) => f.categoria);
  const valores = filas.map((f) => f.total);
  const alto = altoBarras(filas.length);

  return (
    <Envoltura>
      <Grafico
        data={barrasSimples(etiquetas, valores, SERIE_1)}
        layout={layoutBase("Justificaciones por tipo", alto)}
        nombre="por_tipo"
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoImpactoPorCategoria({ reporte }) {
  const filas = reporte?.por_categoria || [];
  if (!filas.some((f) => f.impacto_rbd_horas)) return null;

  return (
    <Paneles>
      {filas.map((fila) => {
        const bruto = fila.impacto_rbd_horas || 0;
        const efectivo = fila.impacto_efectivo_rbd_horas || 0;
        const alto = altoBarras(2);
        const layout = layoutBase(fila.categoria, alto, {
          margin: { l: 10, r: 70, t: 42, b: 42 },
        });
        layout.xaxis.title = {
          text: bruto ? `${fmt((efectivo / bruto) * 100)} % en jornada` : "",
          font: FUENTE_EJE,
          standoff: 6,
        };

        return (
          <Grafico
            key={fila.categoria}
            data={[
              {
                type: "bar",
                orientation: "h",
                y: ["Bruto", "Efectivo"],
                x: [bruto, efectivo],
                marker: { color: [SERIE_1, SERIE_2] },
                text: [fmt(bruto), fmt(efectivo)],
                textposition: "outside",
                textfont: FUENTE_ETIQUETA,
                cliponaxis: false,
                hovertemplate: "%{y}: %{text} h-RBD<extra></extra>",
              },
            ]}
            layout={layout}
            nombre={`impacto_${fila.categoria}`}
            alto={alto}
          />
        );
      })}
    </Paneles>
  );
}

export function GraficoReferencia({ reporte }) {
  const ref = reporte?.referencia;
  if (!ref?.horas_calendario) return null;

  const paneles = [
    {
      titulo: "Horas de calendario",
      etiquetas: ["Disponible en el período", "Con justificación (bruto)"],
      valores: [ref.horas_calendario, ref.impacto_rbd_horas || 0],
      porcentaje: ref.porcentaje_calendario,
    },
    {
      titulo: "Horas de jornada",
      etiquetas: ["Disponible en jornada", "Con justificación (efectivo)"],
      valores: [ref.horas_jornada || 0, ref.impacto_efectivo_rbd_horas || 0],
      porcentaje: ref.porcentaje_jornada,
    },
  ].filter((panel) => panel.valores[0]);

  if (!paneles.length) return null;

  return (
    <Paneles>
      {paneles.map((panel) => {
        const alto = altoBarras(2);
        const layout = layoutBase(panel.titulo, alto, {
          yaxis: ejeCategorias(panel.etiquetas),
          margin: { l: 10, r: 70, t: 42, b: 42 },
        });
        layout.xaxis.title = {
          text: panel.porcentaje == null ? "" : `${fmt(panel.porcentaje)} % del disponible`,
          font: FUENTE_EJE,
          standoff: 6,
        };

        return (
          <Grafico
            key={panel.titulo}
            data={[
              {
                type: "bar",
                orientation: "h",
                y: panel.etiquetas,
                x: panel.valores,
                marker: { color: [CAJA_RELLENO, SERIE_1] },
                text: panel.valores.map((v) => fmt(v)),
                textposition: "outside",
                textfont: FUENTE_ETIQUETA,
                cliponaxis: false,
                hovertemplate: "%{y}: %{text} h-RBD<extra></extra>",
              },
            ]}
            layout={layout}
            nombre={`referencia_${panel.titulo}`}
            alto={alto}
          />
        );
      })}
    </Paneles>
  );
}

export function GraficoDuracionPorRBD({ reporte }) {
  const filas = reporte?.duracion_por_rbd || [];
  if (!filas.length) return null;

  const etiquetas = filas.map((f) => f.categoria);
  const paneles = [
    { titulo: "Horas de reloj por establecimiento", resumenes: filas.map((f) => f.por_rbd) },
    { titulo: "Horas en jornada por establecimiento", resumenes: filas.map((f) => f.en_jornada) },
  ].filter((panel) => panel.resumenes.some((r) => r?.mediana != null));

  if (!paneles.length) return null;

  return (
    <Paneles>
      {paneles.map((panel) => (
        <PanelCajas
          key={panel.titulo}
          titulo={panel.titulo}
          etiquetas={etiquetas}
          resumenes={panel.resumenes}
          nombre={`duracion_${panel.titulo}`}
        />
      ))}
    </Paneles>
  );
}

export function GraficoPorEstado({ reporte }) {
  const filas = reporte?.por_estado || [];
  if (filas.length < 2) return null;

  const etiquetas = filas.map((f) => f.estado);
  const presentes = ORDEN_RESOLUCION.filter((r) => filas.some((f) => f.efecto === r));
  const alto = altoBarras(filas.length) + 40;

  const data = presentes.map((resolucion) => ({
    type: "bar",
    orientation: "h",
    name: resolucion,
    y: etiquetas,
    x: filas.map((f) => (f.efecto === resolucion ? f.total : null)),
    text: filas.map((f) => (f.efecto === resolucion ? fmt(f.total) : "")),
    textposition: "outside",
    textfont: FUENTE_ETIQUETA,
    cliponaxis: false,
    marker: {
      color: COLOR_RESOLUCION[resolucion],
      pattern: {
        shape: PATRON_RESOLUCION[resolucion],
        fillmode: "overlay",
        fgcolor: SUPERFICIE,
        size: 4,
        solidity: 0.3,
      },
    },
    hovertemplate: `%{y}: %{text} (${resolucion})<extra></extra>`,
  }));

  const layout = layoutBase("Justificaciones por etapa del circuito de firmas", alto, {
    yaxis: ejeCategorias(etiquetas),
    barmode: "overlay",
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      y: -0.12,
      xanchor: "center",
      x: 0.5,
      font: FUENTE_ETIQUETA,
    },
    margin: { l: 10, r: 60, t: 42, b: 62 },
  });

  return (
    <Envoltura>
      <Grafico data={data} layout={layout} nombre="por_etapa" alto={alto} />
    </Envoltura>
  );
}

export function GraficoAntiguedadPorEtapa({ reporte }) {
  const filas = (reporte?.por_estado || []).filter((f) => f.antiguedad?.mediana != null);
  if (!filas.length) return null;

  return (
    <Envoltura>
      <PanelCajas
        titulo="Antigüedad en la etapa actual (días)"
        etiquetas={filas.map((f) => f.estado)}
        resumenes={filas.map((f) => f.antiguedad)}
        nombre="antiguedad_por_etapa"
      />
    </Envoltura>
  );
}

export function GraficoDistribucionTiempos({ reporte }) {
  const tiempos = reporte?.tiempos || {};

  const paneles = [
    {
      titulo: "Horas",
      etiquetas: ["Resolución (MTTR)", "Registro del evento"],
      resumenes: [tiempos.mttr_horas, tiempos.latencia_registro_horas],
    },
    {
      titulo: "Días",
      etiquetas: ["Tramitación al cierre", "Antigüedad en la etapa"],
      resumenes: [tiempos.tramitacion_dias, tiempos.antiguedad_etapa_dias],
    },
  ].filter((panel) => panel.resumenes.some((r) => r?.mediana != null));

  if (!paneles.length) return null;

  return (
    <Paneles>
      {paneles.map((panel) => (
        <PanelCajas
          key={panel.titulo}
          titulo={panel.titulo}
          etiquetas={panel.etiquetas}
          resumenes={panel.resumenes}
          nombre={`tiempos_${panel.titulo}`}
        />
      ))}
    </Paneles>
  );
}

export function GraficoDesfaseCreacion({ reporte }) {
  const filas = (reporte?.creacion_vs_evento || []).filter(
    (f) => f.inicio?.mediana != null || f.termino?.mediana != null
  );
  if (!filas.length) return null;

  const etiquetas = filas.map((f) => f.categoria);

  return (
    <Paneles>
      <PanelCajas
        titulo="Al inicio del evento"
        etiquetas={etiquetas}
        resumenes={filas.map((f) => f.inicio)}
        nombre="desfase_inicio"
        referenciaCero
      />
      <PanelCajas
        titulo="Al término del evento"
        etiquetas={etiquetas}
        resumenes={filas.map((f) => f.termino)}
        nombre="desfase_termino"
        referenciaCero
      />
    </Paneles>
  );
}

export function GraficoPermanenciaPorEtapa({ reporte }) {
  const filas = (reporte?.tiempos_por_etapa || []).filter(
    (f) => f.permanencia?.mediana != null
  );
  if (!filas.length) return null;

  return (
    <Envoltura>
      <PanelCajas
        titulo="Permanencia en cada etapa ya superada (días)"
        etiquetas={filas.map((f) => f.estado)}
        resumenes={filas.map((f) => f.permanencia)}
        nombre="permanencia_por_etapa"
      />
    </Envoltura>
  );
}

function MapaCalor({
  mapa,
  titulo,
  nombre,
  etiquetaBarra,
  unidad,
  sufijoEscala = "",
  decimales = null,
}) {
  const filas = mapa?.filas || [];
  const dias = mapa?.dias || [];
  if (!filas.length || !dias.length) return null;

  const alto = Math.max(220, 20 * filas.length + 140);
  const layout = layoutBase(titulo, alto, {
    margin: { l: 10, r: 20, t: 42, b: 50 },
    xaxis: {
      title: { text: "Día del mes", font: FUENTE_EJE, standoff: 6 },
      tickfont: FUENTE_EJE,
      type: "category",
      showgrid: false,
      automargin: true,
    },
    yaxis: {
      tickfont: FUENTE_ETIQUETA,
      automargin: true,
      ticks: "",
      showgrid: false,
      autorange: "reversed",
      type: "category",
    },
  });

  return (
    <Envoltura>
      <Grafico
        data={[
          {
            type: "heatmap",
            x: dias.map((d) => String(d)),
            y: filas.map((f) => recortar(f.nombre_zona, 14)),
            z: filas.map((f) => f.valores.map((v) => (v > 0 ? v : null))),
            text: filas.map((f) => f.valores.map((v) => fmt(v, unidad, decimales))),
            colorscale: SECUENCIAL_AZUL,
            zmin: 0,
            zmax: Math.max(mapa.maximo || 1, 1),
            xgap: 1,
            ygap: 1,
            hoverongaps: false,
            colorbar: {
              title: { text: etiquetaBarra, font: FUENTE_ETIQUETA, side: "right" },
              thickness: 10,
              outlinewidth: 0,
              tickfont: FUENTE_EJE,
              ticksuffix: sufijoEscala,
            },
            hovertemplate: "%{y} · día %{x}: %{text}<extra></extra>",
          },
        ]}
        layout={layout}
        nombre={nombre}
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoMapaCalor({ reporte }) {
  return (
    <MapaCalor
      mapa={reporte?.mapa_calor}
      titulo="RBD afectados por día y zona (solo eventos no programados)"
      nombre="mapa_calor"
      etiquetaBarra="RBD"
      unidad=" RBD"
    />
  );
}

export function GraficoMapaCalorPorcentaje({ reporte }) {
  return (
    <MapaCalor
      mapa={reporte?.mapa_calor_porcentaje}
      titulo="Porcentaje de la zona afectado, por día (solo eventos no programados)"
      nombre="mapa_calor_porcentaje"
      etiquetaBarra="% de la zona"
      unidad=" %"
      sufijoEscala=" %"
      decimales={2}
    />
  );
}

export function GraficoCreacionPorHora({ reporte }) {
  const corte = reporte?.creacion_por_hora || {};
  const valores = corte.valores || [];
  if (!corte.total || !valores.length) return null;

  const horas = (corte.horas || valores.map((_, i) => i)).map((h) =>
    String(h).padStart(2, "0")
  );
  const [desde, hasta] = corte.horario_habil || [];
  const alto = 320;

  const layout = layoutBase("Hora de creación de la justificación (hora de Chile)", alto, {
    margin: { l: 46, r: 20, t: 42, b: 58 },
    bargap: 0.26,
    xaxis: {
      title: {
        text:
          desde == null
            ? "Hora del día (CLT)"
            : `Hora del día (CLT) — la banda marca el horario hábil ${horas[desde]}:00-${String(
                hasta
              ).padStart(2, "0")}:00`,
        font: FUENTE_EJE,
        standoff: 6,
      },
      tickfont: FUENTE_EJE,
      type: "category",
      showgrid: false,
      automargin: true,
    },
    yaxis: {
      title: { text: "Justificaciones creadas", font: FUENTE_EJE, standoff: 8 },
      gridcolor: REJILLA,
      zeroline: false,
      tickfont: FUENTE_EJE,
      automargin: true,
      separatethousands: true,
    },
  });

  if (desde != null && hasta != null) {
    layout.shapes = [
      {
        type: "rect",
        x0: desde - 0.5,
        x1: hasta - 0.5,
        yref: "paper",
        y0: 0,
        y1: 1,
        fillcolor: BANDA_REFERENCIA,
        line: { width: 0 },
        layer: "below",
      },
    ];
  }

  if (corte.hora_peak != null) {
    layout.annotations = [
      {
        x: horas[corte.hora_peak],
        y: valores[corte.hora_peak],
        text: `${horas[corte.hora_peak]}:00 h · ${fmt(valores[corte.hora_peak])}`,
        showarrow: false,
        yshift: 10,
        font: FUENTE_ETIQUETA,
      },
    ];
  }

  return (
    <Envoltura>
      <Grafico
        data={[
          {
            type: "bar",
            x: horas,
            y: valores,
            marker: { color: SERIE_1 },
            hovertemplate: "%{x}:00 h: %{y}<extra></extra>",
          },
        ]}
        layout={layout}
        nombre="creacion_por_hora"
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoPorOperador({ reporte, limite = 10 }) {
  const filas = (reporte?.por_operador || []).slice(0, limite);
  if (filas.length < 2) return null;

  const etiquetas = filas.map((f) => f.nombre_operador || `Operador ${f.id_operador}`);
  const alto = altoBarras(filas.length);

  return (
    <Paneles>
      <Grafico
        data={barrasSimples(etiquetas, filas.map((f) => f.total), SERIE_1)}
        layout={layoutBase("Justificaciones por operador", alto, {
          yaxis: ejeCategorias(etiquetas.map((e) => recortar(e))),
        })}
        nombre="por_operador"
        alto={alto}
      />
      <Grafico
        data={barrasSimples(
          etiquetas,
          filas.map((f) => f.porcentaje_parque || 0),
          SERIE_2,
          " %",
          2
        )}
        layout={layoutBase("% del parque asignado afectado", alto, {
          yaxis: ejeCategorias(etiquetas.map((e) => recortar(e))),
        })}
        nombre="parque_por_operador"
        alto={alto}
      />
    </Paneles>
  );
}
